import { JitsiMeeting } from '@jitsi/web-sdk/lib/components';
import { connect } from 'react-redux';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { User } from 'modules/user/types';
import participantImg from 'assets/svg/call-people-group.svg';
import CustomModal from 'components/customModal/CustomModal';
import ModalShell from 'components/chatModal/ChatModalShell.module.scss';
import InviteFriend from 'views/inviteFriend';
import CallJoinRequestAlerts from 'components/Jitsi/CallJoinRequestAlerts';
import { useHistory } from 'react-router';
import {
  setCallActive,
  touchCallHeartbeat,
  CALL_HEARTBEAT_INTERVAL_MS,
  updateParticipantsInRoom,
  setCallParticipantCount,
  subscribeCallParticipantCount,
  requestCallJoin,
  respondCallJoinRequest,
  clearCallJoinRequest,
  subscribeCallJoinRequests,
  subscribeOwnCallJoinRequest,
  subscribeCallActive,
  CallJoinRequest,
} from 'store/services/firebase-broadcast';
import Styles from 'components/Jitsi/Jitsi.module.scss';
import { getJitsiAvatarUrl } from 'store/services/URL';
import { setLoggedTimeRequest, resetLoggedTime } from 'store/actions/general.actions';
import { useDispatch } from 'react-redux';
import { getLoggedTimeStatus } from 'store/selector/general.selector';
import { generateMeetingLink } from 'utils/meetingLinkUtils';
import api from 'store/services/conversation.service';
import { toast } from 'react-toastify';
import kickIcon from 'assets/svg/kick-icon.svg';
import kickGuestIcon from 'assets/svg/kick-guest.svg';
import { formatUserDateTimeCached } from 'helpers/dateTimeFormat';
import { matchRoomMember, removeGuestFromRoom } from 'helpers/removeGuest';

type participant = { id: string; name: string };

/** Stable Jitsi room id — must match exactly for host and guests (do not use display topic). */
const getJitsiRoomName = (roomPk: number | string | undefined | null): string | null => {
  if (roomPk == null || roomPk === '') return null;
  return `room-${roomPk}`;
};

interface JitsiProps {
  user: User;
  broadCast: any;
  handleClose: Function;
  loggedTime?: string;
}

const Jitsi: FunctionComponent<JitsiProps> = (props: JitsiProps) => {
  const { user, broadCast, handleClose, loggedTime } = props;
  const dispatch = useDispatch();
  const room = broadCast;
  const apiRef = useRef<any>();
  const [host, setHost] = useState<any>({});
  const [name, setName] = useState('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [confirmEndCall, setConfirmEndCall] = useState<boolean>(false);
  const [confirmKickParticipantId, setConfirmKickParticipantId] = useState<string | null>(null);
  const [hostWelcomeAccepted, setHostWelcomeAccepted] = useState(false);
  const [callJoinApproved, setCallJoinApproved] = useState(false);
  const [callJoinRequests, setCallJoinRequests] = useState<CallJoinRequest[]>([]);
  const [showChat, setShowChat] = useState<boolean>(true);
  const [logItems, updateLog] = useState<string[]>([]);
  const [noOfParticipants, setNoOfParticipants] = useState(0);
  const [knockingParticipants, updateKnockingParticipants] = useState<participant[]>([]);
  const history = useHistory();
  const [token, setToken] = useState<string | null>(null);
  const requestRef = useRef(false);
  const hasJoinedConferenceRef = useRef(false);
  const userLeavingRef = useRef(false);
  const wasRejectedRef = useRef(false);
  const pendingNavigateAfterLogRef = useRef(false);
  const hasSentHostEmailRef = useRef(false);

  const jitsiRoomName = getJitsiRoomName(broadCast?.pk);
  const hostPk = broadCast?.host?.pk;
  const isRoomHost = hostPk != null && user?.pk != null && hostPk === user.pk;
  const shouldMountJitsi = Boolean(
    token &&
      jitsiRoomName &&
      (isRoomHost ? hostWelcomeAccepted : callJoinApproved)
  );
  const showHostWelcome = Boolean(isRoomHost && !hostWelcomeAccepted);
  const showGuestWaiting = Boolean(!isRoomHost && token && !callJoinApproved);

  console.log('Jitsi component props:', { user, broadCast, room, host, jitsiRoomName, isRoomHost });

  const closeDialog = () => {
    setShowPopup(false);
    setConfirmEndCall(false);
    setConfirmKickParticipantId(null);
  };

  const kickFromCall = (jitsiParticipantId: string) => {
    try {
      apiRef.current.executeCommand('kickParticipant', jitsiParticipantId);
    } catch (err) {
      console.error('Failed to kick participant:', err);
      toast.error('Could not remove guest.');
    }
  };

  const handleConfirmKick = async () => {
    const jitsiParticipantId = confirmKickParticipantId;
    if (!jitsiParticipantId || !apiRef.current) {
      setConfirmKickParticipantId(null);
      return;
    }
    setConfirmKickParticipantId(null);

    const jitsiParticipants = apiRef.current.getParticipantsInfo?.() || [];
    const jitsiUser = jitsiParticipants.find(
      (p: any) => String(p.participantId || p.id) === String(jitsiParticipantId)
    );
    const member = matchRoomMember(room?.participants || broadCast?.participants, {
      email: jitsiUser?.email,
      displayName: jitsiUser?.displayName || jitsiUser?.name,
    });

    if (member?.pk != null && room?.pk != null) {
      try {
        await removeGuestFromRoom({
          userId: member.pk,
          roomId: room.pk,
          participants: room?.participants || broadCast?.participants,
        });
      } catch (err) {
        console.error('Failed to record eviction:', err);
        toast.error('Could not remove guest.');
        return;
      }
      kickFromCall(jitsiParticipantId);
      return;
    }

    kickFromCall(jitsiParticipantId);
    toast.warn('Guest left the call, but the eviction could not be recorded.');
  };

  const handleHostWelcomeGotIt = () => {
    setHostWelcomeAccepted(true);
  };

  const handleBack = () => {
    setConfirmEndCall(true);
  };

  const handleConfirmLeft = () => {
    setConfirmEndCall(false);
    userLeavingRef.current = true;
    handleReadyToClose();
  };

  useEffect(() => {
    dispatch(resetLoggedTime());
  }, [dispatch]);

  useEffect(() => {
    setHost(room.host);
  }, [room]);

  useEffect(() => {
    console.log('Host state updated:', host);
    if (host !== undefined) {
      setName(host.name);
    }
  }, [host]);

  useEffect(() => {
    if (token !== null || requestRef.current || !jitsiRoomName || hostPk == null || !user?.pk) {
      return;
    }

    requestRef.current = true;
    const affiliation = isRoomHost ? 'owner' : 'member';
    console.log('Generating room token for:', jitsiRoomName, 'affiliation:', affiliation);

    api.getRoomToken({
      moderator: affiliation,
      room: jitsiRoomName,
    })
      .then((res) => {
        console.log('Room token received:', res);
        setToken(res.meeting_token);
      })
      .catch((error) => {
        console.error('Failed to get room token:', error);
        requestRef.current = false;
      });
  }, [token, jitsiRoomName, hostPk, isRoomHost, user?.pk]);

  // Guest: request to join the live call; wait for host ACCEPT / REJECT.
  // Also leave if the host ends the call while the guest is still waiting.
  useEffect(() => {
    if (isRoomHost || !room?.pk || !user?.pk) return undefined;

    let cancelled = false;
    let sawPendingOrApproved = false;
    let sawCallActive = false;
    let leftForEndedCall = false;

    const leaveBecauseCallEnded = () => {
      if (cancelled || leftForEndedCall || hasJoinedConferenceRef.current) return;
      leftForEndedCall = true;
      toast.info('The video call has ended.');
      userLeavingRef.current = true;
      handleClose();
    };

    requestCallJoin(room.pk, user).catch((err) => {
      console.error('Failed to request call join:', err);
    });

    const unsubRequest = subscribeOwnCallJoinRequest(room.pk, user.pk, (req) => {
      if (cancelled) return;
      if (req?.status === 'approved') {
        sawPendingOrApproved = true;
        setCallJoinApproved(true);
      } else if (req?.status === 'rejected' && sawPendingOrApproved) {
        wasRejectedRef.current = true;
        toast.error('The host declined your request to join the call.');
        userLeavingRef.current = true;
        handleClose();
      } else if (req?.status === 'pending') {
        sawPendingOrApproved = true;
      } else if (req == null && sawPendingOrApproved && !hasJoinedConferenceRef.current) {
        // Host ended call and cleared call_join_requests — leave waiting screen.
        leaveBecauseCallEnded();
      }
    });

    const unsubCallActive = subscribeCallActive(room.pk, (active) => {
      if (cancelled) return;
      if (active) {
        sawCallActive = true;
        return;
      }
      // Only leave after we have observed a live call (avoids false leave on empty first snap).
      if (sawCallActive || sawPendingOrApproved) {
        leaveBecauseCallEnded();
      }
    });

    return () => {
      cancelled = true;
      unsubRequest();
      unsubCallActive();
      if (!hasJoinedConferenceRef.current && !wasRejectedRef.current && user?.pk != null && room?.pk != null) {
        clearCallJoinRequest(room.pk, user.pk).catch(() => {});
      }
    };
    // handleClose omitted from deps to avoid re-request loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoomHost, room?.pk, user?.pk]);

  // Host: listen for pending call join requests.
  useEffect(() => {
    if (!isRoomHost || !room?.pk) return undefined;
    return subscribeCallJoinRequests(room.pk, setCallJoinRequests);
  }, [isRoomHost, room?.pk]);

  useEffect(() => () => {
        if (apiRef && apiRef.current) {
          apiRef.current.dispose();
        }
      }, []);

  const handleAcceptCallJoin = (userPk: number | string) => {
    if (!room?.pk) return;
    respondCallJoinRequest(room.pk, userPk, 'approved').catch((err) => {
      console.error('Failed to accept call join:', err);
      toast.error('Could not accept join request.');
    });
  };

  const handleRejectCallJoin = (userPk: number | string) => {
    if (!room?.pk) return;
    respondCallJoinRequest(room.pk, userPk, 'rejected').catch((err) => {
      console.error('Failed to reject call join:', err);
      toast.error('Could not reject join request.');
    });
  };
  const endCallModal = () => (
    <>
      <div className={Styles.leaveCallBody}>
        <p className={Styles.leaveCallMessage}>Are you sure you want to close this room?</p>
      </div>
      <div className={ModalShell.footer}>
        <div className={Styles.leaveCallFooterActions}>
          <button type="button" className={Styles.leaveCallCancelBtn} onClick={closeDialog}>
            Cancel
          </button>
          <button type="button" className={Styles.leaveCallConfirmBtn} onClick={handleConfirmLeft}>
            Confirm
          </button>
        </div>
      </div>
    </>
  );

  const kickGuestModal = () => (
    <>
      <div className={Styles.kickGuestBody}>
        <img src={kickIcon} alt="" className={Styles.kickGuestIcon} />
        <p className={Styles.kickGuestMessage}>
          Are you sure you want to evict this guest from your room?
        </p>
      </div>
      <div className={ModalShell.footer}>
        <div className={Styles.kickGuestFooterActions}>
          <button type="button" className={Styles.kickGuestCancelBtn} onClick={closeDialog}>
            CANCEL
            <span className={Styles.kickGuestBtnX} aria-hidden="true">
              ×
            </span>
          </button>
          <button type="button" className={Styles.kickGuestConfirmBtn} onClick={handleConfirmKick}>
            REMOVE GUEST
            <img src={kickGuestIcon} alt="" className={Styles.kickGuestBtnIcon} />
          </button>
        </div>
      </div>
    </>
  );

  const hostWelcomeModal = () => (
    <>
      <div className={Styles.hostWelcomeBody}>
        <p className={Styles.hostWelcomeSubtitle}>
          {`Welcome ${user?.name || 'Host'}! You are the host of this room.`}
        </p>
        <p className={Styles.hostWelcomeText}>
          You have full control of the room. Approve or reject all users wanting to join, see their
          stats, or kick them out if they&apos;re causing trouble! You can view the host control
          panel for more settings.
        </p>
      </div>
      <div className={ModalShell.footer}>
        <button
          type="button"
          className={Styles.hostWelcomeGotItBtn}
          onClick={handleHostWelcomeGotIt}
        >
          GOT IT! ✓
        </button>
      </div>
    </>
  );

  const printEventOutput = (payload: any) => {
    updateLog((items: string[]) => [...items, JSON.stringify(payload)]);
  };

  const handleAudioStatusChange = (payload: any, feature: any) => {
    if (payload.muted) {
      updateLog((items) => [...items, `${feature} off`]);
    } else {
      updateLog((items) => [...items, `${feature} on`]);
    }
  };

  const handleChatUpdates = (payload: any, ref: any) => {
    if (payload.isOpen || !payload.unreadCount) {
      return;
    }
    ref.current.executeCommand('toggleChat');
    updateLog((items: string[]) => [...items, `you have ${payload.unreadCount} unread messages`]);
  };

  const syncParticipantCount = (api?: any) => {
    const jitsiApi = api ?? apiRef.current;
    if (!jitsiApi || typeof jitsiApi.getNumberOfParticipants !== 'function') {
      return;
    }
    const count = jitsiApi.getNumberOfParticipants();
    setNoOfParticipants(count);
    if (room?.pk == null) return;
    setCallParticipantCount(room.pk, count).catch((err) => {
      console.error('Failed to publish call participant count:', err);
    });
  };

  const handleKnockingParticipant = (payload: participant) => {
    updateLog((items: any) => [...items, JSON.stringify(payload)]);
    updateKnockingParticipants((participants: participant[]) => [...participants, payload]);
  };

  const handleParticipantLeft = (payload: participant) => {
    console.log('Left Participant', payload);
    syncParticipantCount();
  };

  const handleParticipantJoined = (payload: participant) => {
    console.log('Participant Joined', payload, user);
    syncParticipantCount();
  };

  const handleJitsiIFrameRef1 = (parentNode: HTMLDivElement | null) => {
    if (!parentNode) return;
    const applyAllow = (iframe: HTMLIFrameElement) => {
      iframe.setAttribute(
        'allow',
        'camera; microphone; fullscreen; display-capture; autoplay'
      );
    };
    const existing = parentNode.querySelector('iframe');
    if (existing) {
      applyAllow(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const iframe = parentNode.querySelector('iframe');
      if (iframe) {
        applyAllow(iframe);
        observer.disconnect();
      }
    });
    observer.observe(parentNode, { childList: true, subtree: true });
  };

  const handleReadyToClose = () => {
    if (!hasJoinedConferenceRef.current && !userLeavingRef.current) {
      console.warn('Ignoring readyToClose — user has not joined the conference yet');
      return;
    }

    userLeavingRef.current = true;

    // Call hangup first to leave conference cleanly, which may prevent notification
    if (apiRef.current) {
      try {
        apiRef.current.executeCommand('hangup');
      } catch (e) {
        // If hangup fails, just dispose
        if (apiRef.current) {
          apiRef.current.dispose();
        }
      }
    }
    
    // Log time (async, but don't wait for it)
    handleLoggedTime();
    
    // Always call handleClose for cleanup and navigation (both hosts and non-hosts)
    handleClose();
  };

  const handleLoggedTime = () => {
    const startTime = localStorage.getItem('start_time');
    if (startTime !== null) {
      const startMs = parseInt(startTime, 10);
      const endTime = +new Date();
      const totalTime = endTime - startMs;
      pendingNavigateAfterLogRef.current = true;
      dispatch(setLoggedTimeRequest({
        total_spend_time: Math.floor(totalTime / 60000),
        room_id: room.pk.toString(),
        logged_type: 'VIDEO',
        presence_start: startMs,
      }));
    }
    localStorage.removeItem('start_time');
  };

  const handleJoinParticipant = (e: any) => {
    const participants = broadCast.participants;
    const obj = {
      roomId: room.pk,
      user: [...participants,user],
    }
    if (hostPk != null && hostPk !== user.pk) {
      updateParticipantsInRoom(obj).then((e) => console.log('Participant Added', e));
    }
  };

  // Email the room's participants that the video has started — only once, and
  // only when the HOST has actually joined the conference (i.e. clicked Jitsi's
  // "Join meeting"), not merely when the host opened the call page.
  const notifyParticipantsOfHostJoin = async () => {
    if (hasSentHostEmailRef.current) return;
    const emails = (broadCast?.participants || [])
      .map((item: any) => item?.email)
      .filter(Boolean);
    if (emails.length === 0) return;

    hasSentHostEmailRef.current = true;
    try {
      const meetingLink = generateMeetingLink(broadCast, window.location.origin);
      await api.sendMeetingNotificationEmail({
        emailTo: emails.join(','),
        notification: `The video has been started by the Host (${user.name}) for the room ${room.topic}.

Join the call directly: ${meetingLink}

Room: ${room.topic}
Host: ${user.name}
Start Time: ${formatUserDateTimeCached(new Date()) || new Date().toISOString()}

Click the link above to join the video call immediately.`,
        subject: `${room.topic}'s video started by host - Join Now!`,
      });
      console.log('Meeting notification email sent (host joined conference)');
    } catch (error) {
      // Allow a retry on a later join if this attempt failed.
      hasSentHostEmailRef.current = false;
      console.error('Failed to send meeting notification email:', error);
    }
  };

  useEffect(() => {
    if (loggedTime && pendingNavigateAfterLogRef.current) {
      pendingNavigateAfterLogRef.current = false;
      history.push('/conversations-nearby');
    }
  }, [loggedTime, history]);

  const handleVideoConferenceLeft = () => {
    if (!hasJoinedConferenceRef.current) {
      return;
    }
    setShowChat(false);
    handleClose();
  };

  const handleParticipantKickedOut = (payload: any) => {
    // Jitsi notifies ALL clients when anyone is kicked — only leave if we were kicked.
    if (payload?.kicked?.local !== true) {
      return;
    }
    userLeavingRef.current = true;
    handleLoggedTime();
    handleClose();
  };

  const handleApiReady = (apiObj: any, ref: any) => {
    try {
      console.log('Jitsi API ready, setting up event listeners');
      
      const reff: any = ref;
      reff.current = apiObj;
      reff.current.addEventListeners({
        // Listening to events from the external API
        audioMuteStatusChanged: (payload: any) => handleAudioStatusChange(payload, 'audio'),
        videoMuteStatusChanged: (payload: any) => handleAudioStatusChange(payload, 'video'),
        raiseHandUpdated: printEventOutput,
        tileViewChanged: printEventOutput,
        chatUpdated: (payload: any) => handleChatUpdates(payload, ref),
        knockingParticipant: handleKnockingParticipant,
        participantJoined: handleParticipantJoined,
        participantLeft: handleParticipantLeft,
        readyToClose: handleReadyToClose,
        participantKickedOut: handleParticipantKickedOut,
        toolbarButtonClicked: (payload: any) => {
          const key = payload?.key || payload;
          if (
            key === 'hangup' ||
            key === 'hangup-menu' ||
            key === 'hangup-end-meeting' ||
            key === 'end-meeting'
          ) {
            setConfirmEndCall(true);
          }
        },
        participantMenuButtonClick: (payload: any) => {
          if (payload?.key === 'kick' && payload?.participantId) {
            setConfirmKickParticipantId(String(payload.participantId));
          }
        },
        videoConferenceLeft: () => {
          if (apiRef.current) {
            try {
              apiRef.current.executeCommand('hangup');
            } catch (err) {
              if (apiRef.current) {
                apiRef.current.dispose();
              }
            }
          }
          handleVideoConferenceLeft();
        },
      });
      
      // Purple fallback when no real profile — absolute URL on Jitsi host
      try {
        apiObj.executeCommands({
          avatarUrl: getJitsiAvatarUrl(user?.profile),
        });
      } catch (avatarError) {
        console.warn('Failed to set avatar URL:', avatarError);
      }

      apiObj.addEventListener('videoConferenceJoined', (e: any) => {
        console.log('Video conference joined event:', e);
        hasJoinedConferenceRef.current = true;
        try {
          apiObj.executeCommand('setTileView', true);
        } catch (tileErr) {
          console.warn('Failed to enable tile view:', tileErr);
        }
        if (isRoomHost) {
          setCallActive(room.pk, true).catch((err) => {
            console.error('Failed to mark call as live:', err);
          });
          touchCallHeartbeat(room.pk).catch((err) => {
            console.error('Failed to write call heartbeat:', err);
          });
          // Notify participants now that the host is actually in the meeting.
          notifyParticipantsOfHostJoin();
        } else if (hostPk != null && hostPk !== user.pk) {
          const checkParticipant = !!broadCast?.participants?.find((item: any) => item?.pk === user?.pk);
          if (!checkParticipant) {
            handleJoinParticipant(e);
          }
        }
        syncParticipantCount(apiObj);
      });
      
      console.log('Jitsi API setup completed successfully');
    } catch (error) {
      console.error('Error setting up Jitsi API:', error);
    }
  };

  // Host heartbeat while in the conference — clears abandoned VIDEO rooms if tab dies.
  useEffect(() => {
    if (!isRoomHost || !room?.pk) return undefined;

    const tick = () => {
      if (!hasJoinedConferenceRef.current) return;
      touchCallHeartbeat(room.pk).catch(() => {});
    };

    const id = window.setInterval(tick, CALL_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isRoomHost, room?.pk]);

  // Prejoin: show live call size from Firebase (Jitsi API has no conference yet).
  useEffect(() => {
    if (!room?.pk) return undefined;
    return subscribeCallParticipantCount(room.pk, (count) => {
      if (!hasJoinedConferenceRef.current) {
        setNoOfParticipants(count);
      }
    });
  }, [room?.pk]);

  useEffect(() => console.log('Local User Joined', noOfParticipants), [noOfParticipants]);

  if (!token || !jitsiRoomName) {
    return null;
  }

  return (
    <div className={Styles.jitsiMeetingWrapper}>
      {(
        <div className={`d-flex align-items-center justify-content-between ${Styles.headerWrapper}`}>
          <div
            className={`d-flex align-items-center ${Styles.headerLeft}`}
            onClick={handleBack}
            onKeyUp={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBack();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`Leave call, ${room.topic || 'room'}`}
          >
            <i className={`fas fa-chevron-left ${Styles.backIcon}`} aria-hidden="true" />
            <p className={`mb-0 ${Styles.roomName}`} title={room.topic}>{room.topic}</p>
          </div>
          <div className={`d-flex justify-content-between align-items-center ${Styles.headerRight}`}>
            <div className={`d-flex align-items-center ${Styles.inviteSection}`} onClick={() => setShowPopup(true)} onKeyUp={() => setShowPopup(true)} tabIndex={0} role="button" aria-label="Invite friends">
              <p className={`mb-0 ${Styles.inviteLabel}`}>INVITE FRIENDS +</p>
            </div>
            <div className={Styles.separator}>|</div>
            <div className={Styles.hostWrapper}>
              <p className={`mb-0 ${Styles.hostName}`} title={`Host: ${name}`}>Host: {name}</p>
            </div>
            <div className={Styles.hostDot} aria-hidden="true">·</div>
            <div className={`d-flex justify-content-between align-items-center ${Styles.participantCount}`}>
              <img src={participantImg} className={Styles.participantIcon} alt="Participants" />
              <p className={`mb-0 ${Styles.participants}`}>{noOfParticipants}</p>
            </div>
          </div>
        </div>
      )}
      {isRoomHost && hostWelcomeAccepted && (
        <CallJoinRequestAlerts
          requests={callJoinRequests}
          onAccept={handleAcceptCallJoin}
          onReject={handleRejectCallJoin}
        />
      )}
      {showGuestWaiting && (
        <div className={Styles.guestWaiting}>
          <p className={Styles.guestWaitingTitle}>Waiting for the host</p>
          <p className={Styles.guestWaitingText}>
            Your request to join has been sent. You&apos;ll enter the call when the host accepts.
          </p>
        </div>
      )}
      {shouldMountJitsi && (
      <JitsiMeeting
        domain="broadcast.moderatepolitics.org"
        roomName={jitsiRoomName}
        jwt={token}
        userInfo={{
          displayName: user?.name ?? 'Guest',
          email: user?.email ?? '',
        }}
        configOverwrite={{
          disableModeratorIndicator: true,
          prejoinPageEnabled: false,
          prejoinConfig: { enabled: false },
          disableDeepLinking: true,
          disableKick: true,
          enableClosePage: false,
          enableFeedback: false,
          applicationName: ' ',
          defaultLogoUrl: '',
          startWithTileView: true,
          disableTileView: false,
          hideConferenceHeader: true,
          hideConferenceSubject: true,
          disableInviteFunctions: true,
          disableReactions: true,
          disablePolls: true,
          disableProfile: true,
          connectionIndicators: {
            autoHide: false,
            disabled: false,
          },
          // Intercept hangup so we show the same Leave Call modal as the header back control
          buttonsWithNotifyClick: [
            { key: 'hangup', preventExecution: true },
            { key: 'hangup-menu', preventExecution: true },
            { key: 'hangup-end-meeting', preventExecution: true },
            { key: 'end-meeting', preventExecution: true },
          ],
          // Intercept Kick out so we show our Figma confirm modal instead of Jitsi's
          participantMenuButtonsWithNotifyClick: [
            { key: 'kick', preventExecution: true },
          ],
          // Figma toolbox order (hangup placement is limited by Jitsi; CSS styles it)
          toolbarButtons: [
            'chat',
            'participants-pane',
            'desktop',
            'microphone',
            'hangup',
            'camera',
            'tileview',
            'overflowmenu',
          ],
          toolbarConfig: {
            alwaysVisible: true,
            timeout: 4000,
            initialTimeout: 20000,
          },
          notifications: [],
          disabledSounds: ['REACTION_SOUND'],
          dynamicBrandingUrl:
            'https://broadcast.moderatepolitics.org/css/branding.json',
          customTheme: {
            palette: {
              uiBackground: '#000000',
              ui01: '#000000',
              ui02: '#141414',
              ui03: '#292929',
              // Search / form fields (participants pane "Search participants")
              // Opaque tint — rgba over Meet's black wrapper still looks black
              field01: '#f3e8f3',
              inputBackground: '#f3e8f3',
              inputFieldBackground: '#f3e8f3',
              speakerStatsSearchBackground: '#f3e8f3',
              focus01: '#741975',
              inputFieldFocus: '#741975',
              participantActionButton: '#ffffff',
              // Participant "…" context menu (and other Meet overflow menus)
              overflowMenuBackground: '#f3e8f3',
              overflowMenuItemText: '#000000',
              overflowMenuItemIcon: '#741975',
              overflowMenuItemHover: 'rgba(116, 25, 117, 0.12)',
              overflowMenuSeparator: 'rgba(116, 25, 117, 0.25)',
              overflowMenuBorder: '#741975',
              participantItemHover: 'rgba(116, 25, 117, 0.12)',
              participantSectionText: '#741975',
              text02: '#741975',
              action01: '#6b4dff',
              action01Hover: '#7b5cff',
              actionDanger: '#e11d2e',
              actionDangerHover: '#f04352',
              text01: '#ffffff',
              icon01: '#ffffff',
              support05: '#73348C',
              support06: '#6A50D3',
              // Chat bubbles — light purple (not dark gray)
              chatMessageLocal: '#f3e8f3',
              chatMessageRemote: '#f3e8f3',
              chatMessagePrivate: '#f3e8f3',
              chatMessageText: '#000000',
              chatSenderName: '#741975',
            },
            shape: {
              borderRadius: 14,
            },
          },
        }}
        interfaceConfigOverwrite={{
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_PROMOTIONAL_CLOSE_PAGE: false,
          CLOSE_PAGE_GUEST_HINT: false,
          DISABLE_FOCUS_INDICATOR: true,
          DISABLE_DOMINANT_SPEAKER_INDICATOR: false,
          TOOLBAR_ALWAYS_VISIBLE: true,
          TILE_VIEW_MAX_COLUMNS: 5,
          DEFAULT_BACKGROUND: '#000000',
          APP_NAME: ' ',
          NATIVE_APP_NAME: ' ',
          DEFAULT_LOGO_URL: '',
          VIDEO_LAYOUT_FIT: 'both',
          INITIAL_TOOLBAR_TIMEOUT: 20000,
          TOOLBAR_TIMEOUT: 4000,
        }}
        onApiReady={(externalApi) => handleApiReady(externalApi, apiRef)}
        getIFrameRef={handleJitsiIFrameRef1}
      />
      )}
      {showHostWelcome && (
        <CustomModal
          wrapperClass={`${ModalShell.wrapper} ${Styles.hostWelcomeWrapper}`}
          cssClass={ModalShell.body}
          closeDialog={() => {}}
          closeModal={() => {}}
          close={false}
          hideHeader
        >
          {hostWelcomeModal()}
        </CustomModal>
      )}
      {showPopup && (
        <CustomModal
          wrapperClass={ModalShell.wrapper}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
          hideHeader
        >
          <InviteFriend isPopup closePopup={closeDialog} />
        </CustomModal>
      )}
      {confirmEndCall && (
        <CustomModal
          title="Leave Call"
          wrapperClass={ModalShell.wrapper}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={ModalShell.title}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
        >
          {endCallModal()}
        </CustomModal>
      )}
      {confirmKickParticipantId && (
        <CustomModal
          wrapperClass={`${ModalShell.wrapper} ${Styles.kickGuestWrapper}`}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
          hideHeader
        >
          {kickGuestModal()}
        </CustomModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  const loggedTime = getLoggedTimeStatus(state);
  return { loggedTime };
};

export default connect(mapStateToProps, null)(Jitsi);
